import { Button, Toolbar, Typography } from "@material-ui/core";
import Skeleton from "@material-ui/lab/Skeleton";
import { DateTime } from "luxon";
import { FC, useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link as LinkRouter } from "react-router-dom";

import { RelayAction } from "@/api/streamMetadata";
import { LiveUnitMosaicData } from "@/utilities/types/mosaicData";
import { OpenButton } from "@/utilities/types/streaming";

import LightsButton from "../LightsButton/LightsButton";
import SoundsButton from "../SoundsButton/SoundsButton";
import SpeakerTalkdownButton from "../SpeakerTalkdownButton/SpeakerTalkdownButton";
import useStyles from "./LiveStreamFooter.styles";


interface FooterProps {
    mosaicData?: LiveUnitMosaicData;
    alertId?: string; //Change this to in container?
    setIsLive: (_: boolean) => void; //Remove

    /**
     * Flag to remove live unit controls from the footer
     */
    hideControls: boolean;
    allowSpeakerTalkdown?: boolean;
    allowLights?: boolean;
    allowSounds?: boolean;
    disableGoToAlertsButton: boolean;
}

export const LiveStreamFooter: FC<FooterProps> = ({ mosaicData, alertId, setIsLive, hideControls, allowSpeakerTalkdown, allowLights, allowSounds, disableGoToAlertsButton = true }) => {
    const [buttonState, setButtonState] = useState<OpenButton>(OpenButton.None);
    const [clock, setClock] = useState<DateTime>();
    const ref = useRef(null);

    const { t } = useTranslation("translation", { keyPrefix: "components.liveUnitMosaicPage.footer" });
    const classes = useStyles();

    const { cameraId, liveUnitId, locationTimezone, capabilities } = mosaicData?.cameras[0] ?? {};
    const sounds = capabilities?.speakerSounds;

    const lights = mosaicData?.cameras.reduce<RelayAction[]>((acc, camera) => {
        const relayNames = acc.map(ra => ra.relayName);
        const regex = new RegExp(/camera/);

        const dedupedLights: RelayAction[] = camera.capabilities.relayActions.flatMap(light => {
            if (regex.test(light.relayName)) {
                return {
                    ...light,
                    cameraId: camera.cameraId,
                    uiLabel: `${camera.cameraName}: ${light.uiLabel}`,
                };
            } else if (!relayNames.includes(light.relayName)) {
                return {
                    ...light,
                    cameraId: camera.cameraId,
                };
            } else return [];
        });

        return [...acc, ...dedupedLights];
    }, []);

    const handleMenu = (button: OpenButton) => {
        button === buttonState ? setButtonState(OpenButton.None) : setButtonState(button);
    };

    const updateClock = () => {
        setClock(DateTime.now().setZone(locationTimezone!));
    };

    useEffect(() => {
        if (locationTimezone) {
            updateClock();

            const timer = setInterval(updateClock, 5000);

            return () => clearInterval(timer);
        }
    }, [locationTimezone]);

    return (
        <Toolbar className={classes.toolbar}>
            <div className={classes.footerLeft} ref={ref}>
                {mosaicData ?
                    <>
                        {!hideControls &&
                            <>
                                {allowSounds && sounds?.length && cameraId &&
                                    <SoundsButton
                                        cameraId={cameraId}
                                        handleMenu={handleMenu}
                                        open={buttonState === OpenButton.Sounds}
                                        speakerSounds={sounds}
                                    />
                                }

                                {allowSpeakerTalkdown && liveUnitId &&
                                    <SpeakerTalkdownButton
                                        liveUnitId={liveUnitId}
                                        sipInfo={{
                                            sipPhoneNumber: mosaicData.sipPhoneNumber,
                                            sipPin: mosaicData.sipPin,
                                            clientPasscode: mosaicData.clientPasscode,
                                        }}
                                        handleMenu={handleMenu}
                                        open={buttonState === OpenButton.SpeakerTalkDown}
                                    />
                                }

                                {allowLights && lights &&
                                    <LightsButton
                                        lights={lights}
                                        handleMenu={handleMenu}
                                        open={buttonState === OpenButton.Lights}
                                        menuRef={ref}
                                    />
                                }
                            </>
                        }
                    </>
                    :
                    <>
                        <Skeleton className={classes.buttonSkeleton} />
                        <Skeleton className={classes.buttonSkeleton} />
                        <Skeleton className={classes.buttonSkeleton} />
                    </>
                }
            </div>

            <div className={classes.footerMiddle}>
                {mosaicData && clock ?
                    <>
                        <div className={classes.dot} />
                        <Typography className={classes.liveText} data-testid="live-clock">
                            {t("live")}: {clock.toLocaleString(DateTime.TIME_SIMPLE)}
                        </Typography>
                    </>
                    :
                    <Skeleton height={50} width={200} />
                }
            </div>

            <div className={classes.footerRight}>
                {(mosaicData && alertId) ?
                    <LinkRouter
                        style={{ pointerEvents: disableGoToAlertsButton ? "none" : "auto" }}
                        to={{
                            pathname: `/live-unit/${mosaicData.id}/view`,
                            search: `?contextId=${alertId}&&alertId=${alertId}`,
                        }}
                    >
                        <Button
                            aria-label="go-to-alert"
                            disabled={disableGoToAlertsButton}
                            size="large"
                            className={classes.goToAlert}
                            variant="contained"
                            onClick={() => setIsLive(false)}
                        >
                            {t("goToAlerts")}
                        </Button>
                    </LinkRouter>
                    :
                    <Button
                        aria-label="go-to-alert"
                        disabled
                        className={classes.goToAlert}
                        variant="contained"
                    >
                        {t("goToAlerts")}
                    </Button>
                }
            </div>
        </Toolbar>
    );
};
